import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, ExternalLink, Star } from 'lucide-react';

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 } // Reduced for iOS Safari
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const isBusinessHours = () => {
    const hour = currentTime.getHours();
    const day = currentTime.getDay();
    return day >= 1 && day <= 6 && hour >= 8 && hour <= 18; // Monday to Saturday, 8 AM to 6 PM
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Téléphone / WhatsApp',
      items: [
        { 
          value: '07 02 09 45 93',
          link: 'tel:+212702094593',
          primary: true,
          description: 'Service client principal'
        },
        { 
          value: '06 08 11 26 26',
          link: 'tel:+212608112626',
          description: 'Atelier Bouskoura'
        },
        { 
          value: '05 22 02 72 56',
          link: 'tel:+212522027256',
          description: 'Ligne fixe'
        }
      ],
      color: 'from-blue-500 to-blue-600',
      bgColor: 'from-blue-50 to-blue-100'
    },
    {
      icon: Mail,
      title: 'Email',
      items: [
        { 
          value: 'responsable-developpement@faucon-bleu.ma',
          link: 'mailto:responsable-developpement@faucon-bleu.ma',
          primary: true,
          description: 'Développement commercial'
        }
      ],
      color: 'from-green-500 to-emerald-600',
      bgColor: 'from-green-50 to-emerald-100'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      items: [
        { 
          value: 'Atelier de Bouskoura',
          link: 'https://goo.gl/maps/example',
          primary: true,
          description: 'Zone Industrielle,Oulad Saleh (En face de Elbilia School)'
        }
      ],
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'from-purple-50 to-indigo-100'
    }
  ];

  const socialLinks = [
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/atelier_auto_faucon_bleu',
      icon: '/images/instagram.webp',
      color: 'hover:bg-pink-500'
    },
    {
      name: 'LinkedIn',
      url: 'https://linkedin.com/company/fauconbleu',
      icon: '/images/linkedin.webp',
      color: 'hover:bg-blue-600'
    }
  ];

  const businessHours = [
    { day: 'Lundi - Vendredi', hours: '8h00 - 18h00', isToday: currentTime.getDay() >= 1 && currentTime.getDay() <= 5 },
    { day: 'Samedi', hours: '8h00 - 13h00', isToday: currentTime.getDay() === 6 },
    { day: 'Dimanche', hours: 'Fermé', isToday: currentTime.getDay() === 0 }
  ];

  return (
    <footer 
      ref={sectionRef}
      className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden"
      itemScope
      itemType="https://schema.org/ContactPage"
    >
      {/* Background Elements - Simplified for iOS */}
      <div className="absolute inset-0">
        {/* Forum background image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{ backgroundImage: "url('/images/forum.png')" }}
        />
        
        {/* Single gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/95 to-indigo-900/95" />
        
        {/* Simplified animated pattern - Hidden on mobile for iOS performance */}
        <div className="hidden lg:block absolute inset-0 opacity-5">
          <div className="grid grid-cols-12 gap-4 transform rotate-12 scale-150">
            {[...Array(50)].map((_, i) => (
              <div key={i} className="w-2 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        
        {/* Header */}
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-3 py-2 sm:px-4 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium mb-4 sm:mb-6">
            <MessageCircle className="w-4 h-4 mr-2" />
            Contactez-nous
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 px-4">
            Nous sommes là pour{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              vous aider
            </span>
          </h2>
          
          <p className="text-base sm:text-lg lg:text-xl text-blue-100 max-w-3xl mx-auto px-4">
            Une question ? Un devis ? Notre équipe est disponible pour vous accompagner 
            dans tous vos besoins automobiles.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-start">
          
          {/* Contact Information */}
          <div className={`lg:col-span-2 space-y-6 sm:space-y-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            
            {/* Status Bar */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className={`w-3 h-3 rounded-full ${isBusinessHours() ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`} />
                  <span className="text-white font-medium text-sm sm:text-base">
                    {isBusinessHours() ? 'Actuellement ouvert' : 'Actuellement fermé'}
                  </span>
                  <span className="text-blue-200 text-xs sm:text-sm">
                    • Réponse WhatsApp &lt; 5min
                  </span>
                </div>
                <div className="text-white/80 text-sm">
                  {currentTime.toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>

            {/* Contact Methods */}
            <div className="grid gap-4 sm:gap-6">
              {contactInfo.map((contact, index) => {
                const Icon = contact.icon;
                return (
                  <div
                    key={index}
                    className={`
                      bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10
                      transition-all duration-500 hover:bg-white/10 hover:scale-105
                      ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                    `}
                    style={{ transitionDelay: `${(index + 1) * 200}ms` }}
                    itemScope
                    itemType="https://schema.org/ContactPoint"
                  >
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className={`
                        flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-r ${contact.color} 
                        flex items-center justify-center
                      `}>
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4" itemProp="contactType">
                          {contact.title}
                        </h3>
                        
                        <div className="space-y-2 sm:space-y-3">
                          {contact.items.map((item, idx) => (
                            <div key={idx} className="group">
                              <a
                                href={item.link}
                                target={item.link.startsWith('http') ? '_blank' : undefined}
                                rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                                className={`
                                  flex items-center justify-between p-3 rounded-lg sm:rounded-xl transition-all duration-300
                                  ${item.primary 
                                    ? `bg-gradient-to-r ${contact.bgColor} text-gray-900 hover:shadow-lg` 
                                    : 'bg-white/5 text-white hover:bg-white/10'
                                  }
                                `}
                                itemProp={contact.icon === Mail ? 'email' : contact.icon === Phone ? 'telephone' : 'url'}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className={`font-semibold text-sm sm:text-base break-all ${item.primary ? 'text-gray-900' : 'text-white'}`}>
                                    {item.value}
                                  </div>
                                  {item.description && (
                                    <div className={`text-xs sm:text-sm ${item.primary ? 'text-gray-600' : 'text-blue-200'}`}>
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                                <ExternalLink className={`w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0 ${item.primary ? 'text-gray-600' : 'text-white'}`} />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social Media */}
            <div className={`
              bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10
              transition-all duration-500 hover:bg-white/10
              ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
            `} style={{ transitionDelay: '800ms' }}>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Suivez-nous</h3>
              <div className="flex space-x-3 sm:space-x-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`
                      group p-2 sm:p-3 bg-white/10 rounded-lg sm:rounded-xl transition-all duration-300 ${social.color}
                      hover:scale-110 hover:shadow-lg
                    `}
                    aria-label={`Suivre sur ${social.name}`}
                  >
                    <img 
                      src={social.icon} 
                      alt={social.name}
                      className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:scale-110"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Brand & Hours */}
          <div className={`space-y-6 sm:space-y-8 transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            
            {/* Logo & Brand */}
            <div className="text-center">
              <div className="inline-block p-4 sm:p-6 bg-white/10 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-white/20 mb-4 sm:mb-6">
                <img 
                  src="/images/logo-header.png" 
                  alt="Logo Faucon Bleu - Centre de services automobiles"
                  className="h-12 sm:h-16 w-auto mx-auto"
                  loading="lazy"
                  itemProp="logo"
                />
              </div>
              
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Faucon Bleu</h3>
              <p className="text-sm sm:text-base text-blue-200 mb-4 sm:mb-6 px-4">
                Votre partenaire automobile de confiance à Casablanca
              </p>
              
              {/* Rating */}
              <div className="flex items-center justify-center space-x-2 mb-4 sm:mb-6">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-white font-semibold text-sm sm:text-base">4.9/5</span>
                <span className="text-blue-200 text-xs sm:text-sm">(500+ avis)</span>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                <h4 className="text-base sm:text-lg font-bold text-white">Horaires d'ouverture</h4>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {businessHours.map((schedule, index) => (
                  <div 
                    key={index}
                    className={`
                      flex justify-between items-center p-2 sm:p-3 rounded-lg transition-all duration-300
                      ${schedule.isToday 
                        ? 'bg-blue-500/20 border border-blue-400/30' 
                        : 'hover:bg-white/5'
                      }
                    `}
                  >
                    <span className={`font-medium text-sm sm:text-base ${schedule.isToday ? 'text-blue-300' : 'text-white'}`}>
                      {schedule.day}
                      {schedule.isToday && (
                        <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                          Aujourd'hui
                        </span>
                      )}
                    </span>
                    <span className={`text-xs sm:text-sm ${schedule.isToday ? 'text-blue-200' : 'text-blue-300'}`}>
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Partnership */}
            <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-yellow-400/30">
              <div className="text-center">
                <h4 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">Partenaire officiel</h4>
                <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                  <img 
                    src="/images/shell.png" 
                    alt="Shell - Partenaire officiel Faucon Bleu"
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full ring-2 ring-yellow-400"
                    loading="lazy"
                  />
                  <div className="text-left">
                    <div className="font-bold text-white text-sm sm:text-base">Shell</div>
                    <div className="text-xs sm:text-sm text-yellow-200">Huiles premium</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-base sm:text-lg font-bold text-white">Actions rapides</h4>
              
              <a
                href="https://wa.me/212702094593"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl text-sm sm:text-base"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 transition-transform group-hover:scale-110" />
                <span>WhatsApp direct</span>
              </a>
              
              <a
                href="https://reservation.faucon-bleu.ma"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl text-sm sm:text-base"
              >
                🚗 Réserver une vidange
              </a>
              
              <a
                href="tel:+212702094593"
                className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 border-2 border-white/30 text-white font-semibold rounded-lg sm:rounded-xl hover:bg-white/10 transition-all duration-300 text-sm sm:text-base"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Appeler maintenant
              </a>
            </div>
          </div>
        </div>



        {/* Map Section */}
        <div className={`mt-12 sm:mt-16 transition-all duration-1000 delay-1100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-400" />
              Notre localisation
            </h3>
            
            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 items-center">
              {/* Map placeholder */}
              <div className="relative h-48 sm:h-64 bg-white/10 rounded-lg sm:rounded-xl overflow-hidden border border-white/20">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center px-4">
                    <MapPin className="w-8 h-8 sm:w-12 sm:h-12 text-blue-400 mx-auto mb-2" />
                    <div className="text-white font-semibold text-sm sm:text-base">Atelier Bouskoura</div>
                    <div className="text-blue-200 text-xs sm:text-sm">Zone Industrielle</div>
                    <a 
                      href="https://www.google.com/maps?gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIKCAEQABgIGA0YHjIKCAIQABiABBiiBDIKCAMQABiABBiiBDIHCAQQABjvBTIHCAUQABjvBdIBCDI1MzhqMGo5qAIAsAIB&um=1&ie=UTF-8&fb=1&gl=ma&sa=X&geocode=KY_XSZ9mL6YNMdaOmMiUN3EE&daddr=Ennouer+2+S.I.3/47,+Zone+industrielle+Ouled+Saleh+Bouskoura,+Ouled+Saleh+20180"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-3 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                    >
                      <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Voir sur Google Maps
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Directions */}
              <div className="space-y-3 sm:space-y-4">
                <h4 className="text-base sm:text-lg font-semibold text-white">Comment nous trouver</h4>
                <div className="space-y-2 sm:space-y-3 text-blue-200">
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-1 flex-shrink-0">1</div>
                    <div>
                      <div className="font-medium text-white text-sm sm:text-base">Depuis Casablanca centre</div>
                      <div className="text-xs sm:text-sm">Prendre l'autoroute vers Bouskoura (15 min)</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-1 flex-shrink-0">2</div>
                    <div>
                      <div className="font-medium text-white text-sm sm:text-base">Zone Industrielle</div>
                      <div className="text-xs sm:text-sm">Suivre les panneaux "Zone Industrielle Bouskoura"</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-1 flex-shrink-0">3</div>
                    <div>
                      <div className="font-medium text-white text-sm sm:text-base">Parking gratuit</div>
                      <div className="text-xs sm:text-sm">Places disponibles devant l'atelier</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className={`mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-white/10 transition-all duration-1000 delay-1300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 items-center">
            
            {/* Left - Company Info */}
            <div>
              <div className="text-blue-200 text-xs sm:text-sm mb-2">
                © 2025 Faucon Bleu. Tous droits réservés.
              </div>
              <div className="text-white/60 text-xs sm:text-sm">
                Service automobile mobile de confiance à Casablanca depuis 2010.
                Agréé et assuré pour votre tranquillité d'esprit.
              </div>
            </div>
            
            {/* Center - Certifications */}
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-2">
                <img 
                  src="/images/shell.png" 
                  alt="Partenaire Shell" 
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-full opacity-70"
                  loading="lazy"
                />
                <span className="text-white/60 text-xs sm:text-sm">Partenaire agréé</span>
              </div>
              <div className="text-xs text-blue-300">
                Entreprise certifiée • RCS Casablanca • Assurance RC Pro
              </div>
            </div>
            
            {/* Right - Quick Links */}
            <div className="flex flex-wrap gap-2 sm:gap-4 md:justify-end">
              <a href="#mentions-legales" className="text-blue-300 hover:text-white text-xs sm:text-sm transition-colors">
                Mentions légales
              </a>
              <span className="text-white/30">•</span>
              <a href="#confidentialite" className="text-blue-300 hover:text-white text-xs sm:text-sm transition-colors">
                Confidentialité
              </a>
              <span className="text-white/30">•</span>
              <a href="#cgv" className="text-blue-300 hover:text-white text-xs sm:text-sm transition-colors">
                CGV
              </a>
              <span className="text-white/30">•</span>
              <a href="#sitemap" className="text-blue-300 hover:text-white text-xs sm:text-sm transition-colors">
                Plan du site
              </a>
            </div>
          </div>
          
          {/* Final CTA */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/5 text-center">
            <p className="text-blue-200 text-xs sm:text-sm mb-3 sm:mb-4 px-4">
              🚗 Votre véhicule mérite le meilleur service. Contactez-nous dès maintenant !
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center px-4">
              <a
                href="https://wa.me/212702094593"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all duration-300 transform hover:scale-105 text-sm"
              >
                <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                WhatsApp
              </a>
              <a
                href="tel:+212702094593"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 text-sm"
              >
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Appeler
              </a>
              <a
              href="https://reservation.faucon-bleu.ma"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300 text-sm"
              >
                🚗 Réserver
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "AutoRepair",
              "name": "Faucon Bleu",
              "description": "Centre de services automobiles à Casablanca",
              "url": "https://faucon-bleu.ma",
              "logo": "https://fauconbleu.ma/logo.jpeg",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Zone Industrielle",
                "addressLocality": "Bouskoura",
                "addressRegion": "Casablanca",
                "addressCountry": "MA"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 33.428176,
                "longitude": -7.6433821
              },
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+212702094593",
                  "contactType": "Customer Service",
                  "availableLanguage": ["French", "Arabic"],
                  "contactOption": "TollFree"
                },
                {
                  "@type": "ContactPoint",
                  "telephone": "+212608112626",
                  "contactType": "Technical Support",
                  "availableLanguage": ["French", "Arabic"]
                }
              ],
              "email": [
                "contact@faucon-bleu.ma",
                "responsable-developpement@faucon-bleu.ma"
              ],
              "sameAs": [
                "https://instagram.com/atelier_auto_faucon_bleu",
                "https://linkedin.com/company/fauconbleu"
              ],
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  "opens": "08:00",
                  "closes": "18:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": ["Saturday"],
                  "opens": "08:00",
                  "closes": "13:00"
                }
              ],
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "reviewCount": "500"
              }
            }
          })
        }}
      />
    </footer>
  );
};

export default ContactSection;